import React from "react";
import { EnhancedInvoice } from "@/types/invoice";
import { PDFService, RepairShop } from "@/services/PDFService";

interface InvoicePDFProps {
  invoice: EnhancedInvoice;
  repairShop: RepairShop;
  showLogo?: boolean;
}

export const InvoicePDF: React.FC<InvoicePDFProps> = ({
  invoice,
  repairShop,
  showLogo = true,
}) => {
  const data = PDFService.prepareInvoiceData(invoice, repairShop);

  return (
    <div
      className="bg-white p-8 max-w-4xl mx-auto"
      style={{ minHeight: "297mm" }}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div className="flex items-center space-x-4">
          {showLogo && (
            <div className="w-16 h-16">
              <img
                src="/logo.png"
                alt={`${repairShop.name} Logo`}
                className="w-16 h-16 object-contain rounded-lg"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  target.nextElementSibling?.classList.remove("hidden");
                }}
              />
              <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-xl hidden">
                {repairShop.name.charAt(0)}
              </div>
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {repairShop.name}
            </h1>
            {repairShop.address && (
              <p className="text-gray-600">{repairShop.address}</p>
            )}
            {repairShop.phone && (
              <p className="text-gray-600">{repairShop.phone}</p>
            )}
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h2>
          <p className="text-lg font-semibold text-gray-700">
            #{data.invoice.invoiceNumber}
          </p>
          <div className="mt-2">
            <span
              className="px-3 py-1 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: data.invoice.statusColor }}
            >
              {data.invoice.status.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
          <div className="text-gray-700">
            <p className="font-medium">{data.invoice.clientName}</p>
            {data.invoice.clientPhone && <p>{data.invoice.clientPhone}</p>}
            {data.invoice.clientEmail && <p>{data.invoice.clientEmail}</p>}
            {data.invoice.clientAddress && (
              <p className="mt-1">{data.invoice.clientAddress}</p>
            )}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Invoice Details:
          </h3>
          <div className="text-gray-700 space-y-1">
            <div className="flex justify-between">
              <span>Invoice Date:</span>
              <span className="font-medium">
                {data.invoice.formattedCreatedAt}
              </span>
            </div>
            {data.invoice.formattedDueDate && (
              <div className="flex justify-between">
                <span>Due Date:</span>
                <span className="font-medium">
                  {data.invoice.formattedDueDate}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Payment Status:</span>
              <span
                className="px-2 py-1 rounded text-sm font-medium text-white"
                style={{ backgroundColor: data.invoice.paymentStatusColor }}
              >
                {data.invoice.paymentStatus.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-8">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-3 px-2 font-semibold text-gray-900">
                Description
              </th>
              <th className="text-center py-3 px-2 font-semibold text-gray-900">
                Qty
              </th>
              <th className="text-right py-3 px-2 font-semibold text-gray-900">
                Unit Price
              </th>
              <th className="text-right py-3 px-2 font-semibold text-gray-900">
                Discount
              </th>
              <th className="text-right py-3 px-2 font-semibold text-gray-900">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, index) => (
              <tr key={item.id || index} className="border-b border-gray-200">
                <td className="py-3 px-2">
                  <div>
                    <p className="font-medium text-gray-900">
                      {item.productName}
                    </p>
                    {item.description && (
                      <p className="text-sm text-gray-600">
                        {item.description}
                      </p>
                    )}
                  </div>
                </td>
                <td className="text-center py-3 px-2 text-gray-700">
                  {item.quantity}
                </td>
                <td className="text-right py-3 px-2 text-gray-700">
                  {item.formattedUnitPrice}
                </td>
                <td className="text-right py-3 px-2 text-gray-700">
                  {item.discountPercentage > 0
                    ? `${item.discountPercentage}%`
                    : "-"}
                </td>
                <td className="text-right py-3 px-2 font-medium text-gray-900">
                  {item.formattedTotalPrice}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex justify-end mb-8">
        <div className="w-80">
          <div className="space-y-2">
            <div className="flex justify-between py-2">
              <span className="text-gray-700">Subtotal:</span>
              <span className="font-medium text-gray-900">
                {data.totals.formattedSubtotal}
              </span>
            </div>
            {data.totals.discountAmount > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-gray-700">Discount:</span>
                <span className="font-medium text-red-600">
                  -{data.totals.formattedDiscountAmount}
                </span>
              </div>
            )}
            {data.totals.taxAmount > 0 && (
              <div className="flex justify-between py-2">
                <span className="text-gray-700">
                  Tax ({data.totals.taxRate}%):
                </span>
                <span className="font-medium text-gray-900">
                  {data.totals.formattedTaxAmount}
                </span>
              </div>
            )}
            <div className="border-t-2 border-gray-300 pt-2">
              <div className="flex justify-between py-2">
                <span className="text-lg font-semibold text-gray-900">
                  Total:
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {data.totals.formattedTotal}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      {data.invoice.paymentStatus !== "unpaid" && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Payment Information:
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Amount Paid:</span>
                <span className="ml-2 font-medium text-green-600">
                  {PDFService.formatCurrency(data.invoice.paidAmount)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Remaining:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {PDFService.formatCurrency(data.invoice.remainingAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {data.invoice.notes && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes:</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">
              {data.invoice.notes}
            </p>
          </div>
        </div>
      )}

      {/* Terms and Conditions */}
      {data.invoice.termsAndConditions && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Terms and Conditions:
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {data.invoice.termsAndConditions}
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-300 pt-6 mt-8">
        <div className="text-center text-gray-600 text-sm">
          <p>Thank you for your business!</p>
          <p className="mt-1">
            Generated on {PDFService.formatDate(new Date())} | {repairShop.name}
          </p>
        </div>
      </div>
    </div>
  );
};

export default InvoicePDF;
